import { Controller } from "@hotwired/stimulus"

// Single-page create flow: section 1 picks prospect vs client (with inline account
// search); sections 2–3 stay disabled until section 1 is satisfied.
export default class extends Controller {
  static targets = [
    "laterSection",
    "submitButton",
    "accountSearchPanel",
    "detailsFields",
    "detailsHint",
    "kindNew",
    "kindExisting",
    "kindPicker",
    "newProspectSummary"
  ]
  static values = {
    initialUnlocked: { type: Boolean, default: false }
  }

  static disabledClasses = ["opacity-50", "pe-none", "user-select-none"]

  connect() {
    this.kind = this.inferKind()
    this.dealConflictActive = false
    this.applyKindUi()

    if (this.initialUnlockedValue) {
      this.collapseKindPicker()
      this.unlockLaterSections()
    } else {
      this.updateSectionState()
    }
    this.updateDetailsHint()
  }

  inferKind() {
    if (this.hasKindExistingTarget && this.kindExistingTarget.checked) return "existing"
    if (this.hasKindNewTarget && this.kindNewTarget.checked) return "new"

    if (this.accountIdInput()?.value) return "existing"
    return null
  }

  selectKind() {
    this.kind = this.inferKind()
    this.applyKindUi()
    if (this.kind === "new") this.collapseKindPicker()
    this.updateSectionState()
    this.updateDetailsHint()
  }

  editKind() {
    this.clearAccountFields()
    this.hideAccountSearch()
    this.hideNewProspectSummary()
    this.showKindPicker()
    if (this.hasKindNewTarget) this.kindNewTarget.checked = false
    if (this.hasKindExistingTarget) this.kindExistingTarget.checked = false
    this.kind = null
    this.updateSectionState()
    this.updateDetailsHint()
  }

  applyKindUi() {
    if (this.kind === "existing") {
      this.hideNewProspectSummary()
      if (this.accountIdInput()?.value) {
        this.hideAccountSearch()
      } else {
        this.showAccountSearch()
      }
    } else if (this.kind === "new") {
      this.hideAccountSearch()
      this.clearAccountFields()
      this.showNewProspectSummary()
    } else {
      this.hideAccountSearch()
      this.hideNewProspectSummary()
    }
  }

  collapseKindPicker() {
    if (this.hasKindPickerTarget) this.kindPickerTarget.classList.add("d-none")
  }

  showKindPicker() {
    if (this.hasKindPickerTarget) this.kindPickerTarget.classList.remove("d-none")
  }

  showNewProspectSummary() {
    if (!this.hasNewProspectSummaryTarget) return
    this.newProspectSummaryTarget.classList.remove("d-none")
    this.newProspectSummaryTarget.classList.add("d-flex")
  }

  hideNewProspectSummary() {
    if (!this.hasNewProspectSummaryTarget) return
    this.newProspectSummaryTarget.classList.add("d-none")
    this.newProspectSummaryTarget.classList.remove("d-flex")
  }

  accountSelected() {
    this.hideAccountSearch()
    this.collapseKindPicker()
    this.updateSectionState()
    this.updateDetailsHint()
  }

  accountCleared() {
    this.updateSectionState()
    this.updateDetailsHint()
  }

  dealLinked() {
    this.dealConflictActive = false
    this.updateSectionState()
    this.updateDetailsHint()
  }

  dealConflict() {
    this.dealConflictActive = true
    this.updateSectionState()
  }

  dealCleared() {
    this.dealConflictActive = false
    this.updateSectionState()
    this.updateDetailsHint()
  }

  guardSubmit(event) {
    if (!this.kindStepComplete() || this.dealConflictActive) {
      event.preventDefault()
    }
  }

  // Native inputs and Tom Select stay in sync. The accounting multi-selects
  // init while the details fieldset is disabled, so unlocking the fieldset
  // alone leaves their Tom Select wrappers unusable.
  setFieldsEnabled(root, enabled) {
    root.querySelectorAll("input, select, textarea, button").forEach((field) => {
      if (field.type === "hidden") return
      field.disabled = !enabled
      if (field.tomselect) {
        if (enabled) field.tomselect.enable()
        else field.tomselect.disable()
      }
    })
  }

  kindStepComplete() {
    if (!this.kind) return false
    if (this.kind === "new") return true
    return Boolean(this.accountIdInput()?.value)
  }

  updateSectionState() {
    const enabled = this.kindStepComplete()
    this.laterSectionTargets.forEach(section => {
      this.constructor.disabledClasses.forEach(className => {
        section.classList.toggle(className, !enabled)
      })
      this.setFieldsEnabled(section, enabled)
    })

    if (this.hasDetailsFieldsTarget) {
      this.detailsFieldsTarget.disabled = !enabled
      this.setFieldsEnabled(this.detailsFieldsTarget, enabled)
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !enabled || this.dealConflictActive
    }
  }

  unlockLaterSections() {
    this.laterSectionTargets.forEach(section => {
      this.constructor.disabledClasses.forEach(className => {
        section.classList.remove(className)
      })
      this.setFieldsEnabled(section, true)
    })
    if (this.hasDetailsFieldsTarget) {
      this.detailsFieldsTarget.disabled = false
      this.setFieldsEnabled(this.detailsFieldsTarget, true)
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = this.dealConflictActive
    }
    this.updateDetailsHint()
  }

  updateDetailsHint() {
    if (!this.hasDetailsHintTarget) return

    const hasAccount = Boolean(this.accountIdInput()?.value)
    const hasDeal = Boolean(this.dealIdInput()?.value)

    if (hasAccount && hasDeal) {
      this.detailsHintTarget.textContent = "Filled from the client record, then HubSpot. Edit anything that looks off."
    } else if (hasAccount) {
      this.detailsHintTarget.textContent = "Filled from the client record. Edit anything that looks off."
    } else if (hasDeal) {
      this.detailsHintTarget.textContent = "Filled from HubSpot. Edit anything that looks off."
    } else if (this.kindStepComplete()) {
      this.detailsHintTarget.textContent = "Nothing linked yet — fill in the details below."
    } else {
      this.detailsHintTarget.textContent = "Complete step 1 to fill in the details below."
    }
  }

  showAccountSearch() {
    if (this.hasAccountSearchPanelTarget) {
      this.accountSearchPanelTarget.classList.remove("d-none")
    }
  }

  hideAccountSearch() {
    if (this.hasAccountSearchPanelTarget) {
      this.accountSearchPanelTarget.classList.add("d-none")
    }
  }

  clearAccountFields() {
    const accountId = this.accountIdInput()
    if (accountId) accountId.value = ""

    const summary = this.element.querySelector('[data-sales-account-picker-target="selectedSummary"]')
    if (summary) {
      summary.classList.add("d-none")
      summary.classList.remove("d-flex")
    }

    const query = this.element.querySelector('[data-sales-account-picker-target="query"]')
    if (query) query.value = ""

    const results = this.element.querySelector('[data-sales-account-picker-target="results"]')
    if (results) {
      results.innerHTML = ""
      results.classList.add("d-none")
    }
  }

  accountIdInput() {
    return this.element.querySelector('[data-sales-account-picker-target="accountId"]')
  }

  dealIdInput() {
    return this.element.querySelector('[data-sales-hubspot-deal-picker-target="dealId"]')
  }
}
